import { Reveal, Chapter } from "@/components/lunar/Reveal";

const BUNDLES = [
    {
        id: "starter",
        name: "Starter bundle",
        tagline: "The simple, low-cost way to introduce Lunar to your venue.",
        desc: "Designed for cafés, gyms, hotels, coworking spaces and smaller venues.",
        includes: ["4 × tent cards", "50 × flyers"],
        price: "£59 + VAT",
        image: "/assets/bundles/starter-bundle-lunar.webp",
        alt: "Lunar starter bundle with four tent cards and fifty flyers",
    },
    {
        id: "pub",
        name: "Pub & bar bundle",
        tagline: "Lunar visibility from the entrance through to the tables.",
        desc: "Designed to provide Lunar visibility throughout a pub or bar.",
        includes: ["1 × pull-up banner", "6 × tent cards", "50 × beer mats/coasters", "100 × flyers"],
        price: "£199 + VAT",
        image: "/assets/bundles/pub-bar-bundle-lunar.webp",
        alt: "Lunar pub and bar bundle with pull-up banner, tent cards, beer mats and flyers",
    },
    {
        id: "networking",
        name: "Networking venue bundle",
        desc: "Designed for coworking spaces, business lounges, member clubs and professional communities.",
        includes: ["1 × pull-up banner", "6 × tent cards", "100 × flyers"],
        price: "£179 + VAT",
        image: "/assets/bundles/networking-venue-bundle-lunar.webp",
        alt: "Lunar networking venue bundle with pull-up banner, tent cards and flyers",
    },
];

const BESPOKE_BUNDLE = {
    id: "launch",
    name: "Bespoke venue bundle",
    tagline: "Chat with us to create your own bundle.",
    desc: "Every venue is different — tell us what you need and we’ll put together a bespoke Lunar package, with co-branded materials alongside your own branding.",
    includes: ["A-board", "Poster", "Pull-up banner", "Tent cards", "Flyers", "Stickers", "Co-branded designs"],
    price: "Contact us for pricing",
    image: "/assets/bundles/bespoke-bundle-lunar.webp",
    alt: "Lunar bespoke bundle with A-board, poster, pull-up banner, tent cards, flyers, stickers and co-branded example",
    ctaLabel: "Chat with us",
};

const BundleCard = ({ bundle, onRequest, dark = false }) => (
    <div
        data-testid={`bundle-card-${bundle.id}`}
        className={`flex flex-col rounded-3xl border p-6 sm:p-8 ${
            dark ? "border-[#3E4F1B] bg-[#2C3618]" : "border-[#E5E3DC] bg-[#F9F8F4]"
        }`}
    >
        <img
            src={bundle.image}
            alt={bundle.alt}
            data-testid={`bundle-image-${bundle.id}`}
            className={`w-full rounded-2xl bg-[#F2F0EA] object-contain ${
                dark ? "aspect-[16/10]" : "aspect-[4/3]"
            }`}
            loading="lazy"
        />
        <div className="mt-6 flex flex-1 flex-col">
            <h3 className={`font-display text-2xl font-semibold ${dark ? "text-[#F9F8F4]" : "text-[#1D201A]"}`}>
                {bundle.name}
            </h3>
            {bundle.tagline && (
                <p className={`mt-2 text-sm font-medium ${dark ? "text-[#A8B888]" : "text-[#4A5D23]"}`}>
                    {bundle.tagline}
                </p>
            )}
            <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-[#D5D8CB]" : "text-[#5C5F58]"}`}>
                {bundle.desc}
            </p>
            <ul className={`mt-5 space-y-1.5 border-t pt-4 text-sm ${dark ? "border-[#3E4F1B] text-[#D5D8CB]" : "border-[#E5E3DC] text-[#3E413B]"}`}>
                {bundle.includes.map((item) => (
                    <li key={item} className="flex gap-2">
                        <span className="text-[#899B6A]">·</span>
                        {item}
                    </li>
                ))}
            </ul>
            <p className={`font-display mt-5 text-2xl font-semibold ${dark ? "text-[#D5D8CB]" : "text-[#2C3618]"}`}>
                {bundle.price}
            </p>
            <button
                data-testid={`bundle-request-${bundle.id}`}
                onClick={() => onRequest(bundle.name)}
                className={`mt-4 w-full rounded-full py-3 text-sm font-bold transition-[background-color,transform] duration-300 hover:-translate-y-[2px] ${
                    dark
                        ? "bg-[#899B6A] text-[#171816] hover:bg-[#A8B888]"
                        : "bg-[#2C3618] text-[#F9F8F4] hover:bg-[#4A5D23]"
                }`}
            >
                {bundle.ctaLabel || "Request this bundle"}
            </button>
        </div>
    </div>
);

export const Bundles = ({ onRequest }) => (
    <section id="bundles" className="mx-auto max-w-6xl scroll-mt-8 px-6 py-20" data-testid="bundles-section">
        <Reveal>
            <Chapter index="01" label="Choose your Lunar kit" />
            <h2 className="font-display mt-5 text-4xl font-medium leading-[1.1] tracking-tight text-[#1D201A] sm:text-5xl">
                Lunar bundles.
            </h2>
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BUNDLES.map((bundle, i) => (
                <Reveal key={bundle.id} delay={i * 0.08}>
                    <BundleCard bundle={bundle} onRequest={onRequest} />
                </Reveal>
            ))}
            <Reveal delay={0.15} className="sm:col-span-2 lg:col-span-3">
                <BundleCard
                    onRequest={onRequest}
                    dark
                    bundle={BESPOKE_BUNDLE}
                />
            </Reveal>
        </div>
    </section>
);
